import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchProductInquiries,
  productInquiryForm,
  PRODUCT_INQUIRY_PRODUCTS,
  type ProductInquiry,
  type ProductInquiryProduct,
} from '../../api/productInquiries';
import CampaignAttributionDetails from './CampaignAttributionDetails';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type ProductFilter = 'all' | ProductInquiryProduct;

const ProductInquiryDetail: React.FC<{ row: ProductInquiry; onClose: () => void }> = ({
  row,
  onClose,
}) => {
  const form = productInquiryForm(row.product);
  const needsLabel = form.needsLabel ?? (row.needs ? 'Message' : null);
  return (
    <div className="website-lead-detail" style={{ marginTop: 16 }}>
      <h3 className="website-lead-subheading">
        {`${row.first_name} ${row.last_name}`.trim() || row.company || 'Inquiry'}
      </h3>
      <dl className="website-lead-details">
        <div>
          <dt>Form</dt>
          <dd>{form.label}</dd>
        </div>
        <div>
          <dt>Company</dt>
          <dd>{row.company || '—'}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{row.phone ? <a href={`tel:${row.phone}`}>{row.phone}</a> : '—'}</dd>
        </div>
        {form.asksSmsConsent ? (
          <div>
            <dt>SMS marketing consent</dt>
            <dd>{row.sms_marketing_consent ? 'Yes' : 'No'}</dd>
          </div>
        ) : null}
        {form.asksIndustry ? (
          <div>
            <dt>Industry</dt>
            <dd>{row.industry || '—'}</dd>
          </div>
        ) : null}
        {form.asksPropertyType ? (
          <div>
            <dt>Property type</dt>
            <dd>{row.property_type || '—'}</dd>
          </div>
        ) : null}
        {form.asksFacilityLocation ? (
          <div>
            <dt>Facility location</dt>
            <dd>{row.facility_location || '—'}</dd>
          </div>
        ) : null}
        {form.asksFacilityType ? (
          <div>
            <dt>Facility type</dt>
            <dd>{row.facility_type || '—'}</dd>
          </div>
        ) : null}
        <div>
          <dt>{form.useCasesLabel}</dt>
          <dd>{(row.use_cases ?? []).join(', ') || '—'}</dd>
        </div>
        {form.asksEstimatedPayload ? (
          <div>
            <dt>Estimated payload</dt>
            <dd>{row.estimated_payload || '—'}</dd>
          </div>
        ) : null}
        <div>
          <dt>Received</dt>
          <dd>{formatDateTime(row.created_at)}</dd>
        </div>
      </dl>
      {needsLabel ? (
        <>
          <h4 className="website-lead-subheading">{needsLabel}</h4>
          <p className="website-lead-message">{row.needs || '—'}</p>
        </>
      ) : null}
      <CampaignAttributionDetails row={row} />
      <button type="button" className="secondary-button" onClick={onClose}>
        Close
      </button>
    </div>
  );
};

const ProductInquiriesSection: React.FC<{ userId: number }> = ({ userId }) => {
  const [rows, setRows] = useState<ProductInquiry[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState<ProductFilter>('all');
  const [selected, setSelected] = useState<ProductInquiry | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await fetchProductInquiries(userId);
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load inquiries.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const counts = useMemo(() => {
    const out = new Map<string, number>();
    for (const row of rows) out.set(row.product, (out.get(row.product) ?? 0) + 1);
    return out;
  }, [rows]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (productFilter !== 'all' && row.product !== productFilter) return false;
      if (!term) return true;
      return [
        row.first_name,
        row.last_name,
        row.company,
        row.email,
        row.phone,
        row.industry,
        row.facility_location,
        row.property_type,
        row.facility_type,
        row.estimated_payload,
        row.needs,
        ...(row.use_cases ?? []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [rows, search, productFilter]);

  const shown = selected && visible.some((row) => row.id === selected.id) ? selected : null;

  return (
    <section className="events-table-section" aria-labelledby="product-inquiries-heading">
      <h2 className="events-section-title" id="product-inquiries-heading">
        Product Inquiries <span className="events-section-count">({rows.length})</span>
      </h2>
      <p className="page-subtitle website-lead-intro">
        Submissions from the TIM-E, RaaSP, RUN-R and BRON-E interest forms on the website.
      </p>
      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search name, company, email, answers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search product inquiries"
        />
        <div className="website-lead-filters" role="group" aria-label="Filter by form">
          {(['all', ...PRODUCT_INQUIRY_PRODUCTS] as ProductFilter[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`website-lead-filter${productFilter === p ? ' website-lead-filter--active' : ''}`}
              aria-pressed={productFilter === p}
              onClick={() => setProductFilter(p)}
            >
              {p === 'all'
                ? `All (${rows.length})`
                : `${productInquiryForm(p).label} (${counts.get(p) ?? 0})`}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="page-subtitle">Loading…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table">
            <thead>
              <tr>
                <th scope="col">Form</th>
                <th scope="col">Name</th>
                <th scope="col">Company</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
                <th scope="col">Interested in</th>
                <th scope="col">Received</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="events-empty-cell">
                    {rows.length === 0 ? 'No product inquiries yet.' : 'No inquiries match.'}
                  </td>
                </tr>
              ) : (
                visible.map((row) => (
                  <tr key={row.id} onClick={() => setSelected(row)} style={{ cursor: 'pointer' }}>
                    <td data-label="Form">
                      <span className="website-lead-tag">{productInquiryForm(row.product).label}</span>
                    </td>
                    <td data-label="Name">{`${row.first_name} ${row.last_name}`.trim() || '—'}</td>
                    <td data-label="Company">{row.company || '—'}</td>
                    <td data-label="Email">{row.email || '—'}</td>
                    <td data-label="Phone" style={{ whiteSpace: 'nowrap' }}>
                      {row.phone || '—'}
                    </td>
                    <td data-label="Interested in">{(row.use_cases ?? []).join(', ') || '—'}</td>
                    <td data-label="Received">{formatDateTime(row.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {shown ? <ProductInquiryDetail row={shown} onClose={() => setSelected(null)} /> : null}
    </section>
  );
};

export default ProductInquiriesSection;
